import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    contact_email: '',
    sponsorship_interests: []
  });
  const [submitting, setSubmitting] = useState(false);

  const interestOptions = [
    'Product Reviews',
    'Sponsored Tutorials',
    'Brand Collaborations',
    'Affiliate Marketing',
    'Brand Ambassador',
    'Event Sponsorships'
  ];

  const toggleInterest = (interest) => {
    if (formData.sponsorship_interests.includes(interest)) {
      setFormData({
        ...formData,
        sponsorship_interests: formData.sponsorship_interests.filter(i => i !== interest)
      });
    } else {
      setFormData({
        ...formData,
        sponsorship_interests: [...formData.sponsorship_interests, interest]
      });
    }
  };

  const handleComplete = async () => {
    if (!formData.contact_email) {
      toast.error('Please enter your contact email');
      return;
    }

    if (formData.sponsorship_interests.length === 0) {
      toast.error('Please select at least one sponsorship interest');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/creators/onboarding/complete`,
        formData,
        { withCredentials: true }
      );
      toast.success('Onboarding completed!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast.error('Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Contact Information' },
    { number: 2, title: 'Sponsorship Interests' }
  ];

  const progress = (step / steps.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-2xl w-full p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="onboarding-heading">
            Complete Your Profile
          </h1>
          <p className="text-slate-600">Let's get you set up to discover sponsorship opportunities</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.map((s, index) => (
              <div key={s.number} className="flex items-center gap-2">
                {step > s.number ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : step === s.number ? (
                  <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {s.number}
                  </div>
                ) : (
                  <Circle className="w-6 h-6 text-slate-300" />
                )}
                <span className={`text-sm font-medium ${
                  step >= s.number ? 'text-slate-900' : 'text-slate-400'
                }`}>{s.title}</span>
              </div>
            ))}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-600 rounded-full transition-all duration-300" style={{width: `${progress}%`}}></div>
          </div>
          <p className="text-right text-sm text-slate-600 mt-2">{Math.round(progress)}% Complete</p>
        </div>

        {/* Step 1: Contact Information */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="contact_email">Contact Email for Sponsorships *</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="your@email.com"
                className="mt-2"
                data-testid="contact-email-input"
              />
              <p className="text-sm text-slate-500 mt-1">Brands will use this email to reach out to you</p>
            </div>

            <Button
              onClick={() => setStep(2)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
              data-testid="next-step-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Sponsorship Interests */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <Label>What types of sponsorships are you interested in? *</Label>
              <p className="text-sm text-slate-500 mb-4">Select all that apply</p>
              <div className="grid grid-cols-2 gap-3">
                {interestOptions.map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => toggleInterest(interest)}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      formData.sponsorship_interests.includes(interest)
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-2">
                      {formData.sponsorship_interests.includes(interest) ? (
                        <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-300" />
                      )}
                      <span className="text-sm font-medium text-slate-900">{interest}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12"
                data-testid="back-btn"
              >
                Back
              </Button>
              <Button
                onClick={handleComplete}
                disabled={submitting}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white h-12"
                data-testid="complete-onboarding-btn"
              >
                {submitting ? 'Completing...' : 'Complete Setup'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OnboardingWizard;
